/**
 * Client for the agent chat surface (`/v1/agent`).
 *
 * Differs from the legacy `chat.ts` stream client in three ways that matter:
 *  - the transport is the typed SSE contract of docs/plan/03-chat-poc.md
 *    section 3.3 (`event:`, `id:` and `data:` fields, `: keepalive` comments),
 *    not a stream of data-only JSON chunks;
 *  - the caller's `AbortSignal` is actually passed to `fetch` AND used to
 *    cancel the reader, so leaving the page stops the request;
 *  - 403 and 429 are turned into a typed `AgentApiError` carrying the status,
 *    the backend's error `code` and the usage payload, the same fields
 *    `ChatPage` reads today.
 */
import { api, getAuthToken } from "./client";
import type {
  AgentChatRequest,
  AgentConversationDetail,
  AgentConversationsResponse,
  AgentDeactivateResponse,
  AgentEvent,
  AgentEventType,
  AgentToolsResponse,
} from "../../types/agent-chat";

/** Path of the streaming route, appended to `api.defaults.baseURL`. */
const STREAM_PATH = "/agent/chat/stream";

/** Event names the UI understands. Anything else on the wire is ignored. */
const KNOWN_EVENT_TYPES: ReadonlySet<string> = new Set<AgentEventType>([
  "meta",
  "guardrail",
  "tool_call",
  "tool_result",
  "sources",
  "token",
  "citation",
  "artifact",
  "done",
  "error",
]);

/** Terminal events: the server sends nothing after one of these. */
const TERMINAL_EVENT_TYPES: ReadonlySet<string> = new Set<AgentEventType>(["done", "error"]);

/**
 * An HTTP failure that happened before the stream started.
 *
 * `code` and `usage` mirror the legacy client so the existing 429 banner and
 * 403 "access denied" bubble can be driven from the same fields.
 */
export class AgentApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly usage: unknown;

  constructor(message: string, status: number, code: string, usage: unknown = null) {
    super(message);
    this.name = "AgentApiError";
    this.status = status;
    this.code = code;
    this.usage = usage;
  }
}

/** One dispatched server-sent event, before its `data` is parsed. */
export interface SseFrame {
  /** the `event:` field, or null when the server sent none */
  event: string | null;
  /** the `id:` field (the per-turn sequence number), or null */
  id: string | null;
  /** the `data:` field(s), joined with "\n" */
  data: string;
}

/**
 * Incremental server-sent-event framer.
 *
 * Feeding it the same transcript split at any byte boundary yields the same
 * frames, which is the whole point: `reader.read()` gives arbitrary slices, and
 * a frame regularly straddles two of them.
 *
 * Follows the WHATWG event-stream rules the backend relies on: lines end with
 * "\n" or "\r\n", a blank line dispatches, a line starting with ":" is a
 * comment (the 15-second keepalive) and is ignored, and a field is
 * "name:value" with one optional space after the colon. A trailing event with
 * no blank line to close it is discarded, exactly like the spec says, so a
 * truncated stream never produces a half-parsed event.
 */
export class SseFrameParser {
  private buffer = "";
  private dataLines: string[] = [];
  private eventName: string | null = null;
  private lastId: string | null = null;
  private sawField = false;

  /** Feed one decoded chunk; returns the frames it completed (possibly none). */
  push(text: string): SseFrame[] {
    this.buffer += text;
    const frames: SseFrame[] = [];

    let newline = this.buffer.indexOf("\n");
    while (newline !== -1) {
      let line = this.buffer.slice(0, newline);
      this.buffer = this.buffer.slice(newline + 1);
      if (line.endsWith("\r")) {
        line = line.slice(0, -1);
      }
      const frame = this.handleLine(line);
      if (frame) {
        frames.push(frame);
      }
      newline = this.buffer.indexOf("\n");
    }

    return frames;
  }

  /** Everything still buffered when the stream ends. Always empty by design. */
  flush(): SseFrame[] {
    this.buffer = "";
    this.reset();
    return [];
  }

  private handleLine(line: string): SseFrame | null {
    if (line === "") {
      return this.dispatch();
    }
    if (line.startsWith(":")) {
      return null; // comment, e.g. ": keepalive"
    }

    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) {
      value = value.slice(1);
    }

    switch (field) {
      case "event":
        this.eventName = value;
        this.sawField = true;
        break;
      case "data":
        this.dataLines.push(value);
        this.sawField = true;
        break;
      case "id":
        this.lastId = value;
        this.sawField = true;
        break;
      default:
        break; // unknown field (including "retry"): ignored
    }
    return null;
  }

  private dispatch(): SseFrame | null {
    if (!this.sawField || this.dataLines.length === 0) {
      this.reset();
      return null;
    }
    const frame: SseFrame = {
      event: this.eventName,
      id: this.lastId,
      data: this.dataLines.join("\n"),
    };
    this.reset();
    return frame;
  }

  private reset(): void {
    this.dataLines = [];
    this.eventName = null;
    this.lastId = null;
    this.sawField = false;
  }
}

/**
 * Turn one frame into a typed event, or null when it should be ignored.
 *
 * The discriminator is the `type` in the JSON payload (the backend always sets
 * it); the `event:` field is only a fallback. Unparseable data and event names
 * this build does not know are dropped rather than thrown, so a newer server
 * adding an event never breaks an older tab.
 */
export function frameToEvent(frame: SseFrame): AgentEvent | null {
  let payload: unknown;
  try {
    payload = JSON.parse(frame.data);
  } catch {
    return null;
  }
  if (payload === null || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const type = typeof record.type === "string" ? record.type : frame.event;
  if (typeof type !== "string" || !KNOWN_EVENT_TYPES.has(type)) {
    return null;
  }
  return { ...record, type } as AgentEvent;
}

/** True once the turn is over: nothing useful follows a `done` or an `error`. */
function isTerminal(event: AgentEvent): boolean {
  return TERMINAL_EVENT_TYPES.has(event.type);
}

function isAbort(error: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) {
    return true;
  }
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "AbortError"
  );
}

/** Read the FastAPI error body and raise the typed error the UI reacts to. */
async function raiseHttpError(response: Response): Promise<never> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    /* an empty or non-JSON body is normal for a proxy error */
  }
  const detail = (body as { detail?: unknown } | null)?.detail;
  const structured = (typeof detail === "object" && detail !== null ? detail : {}) as {
    message?: unknown;
    code?: unknown;
    usage?: unknown;
  };
  const message =
    typeof structured.message === "string"
      ? structured.message
      : typeof detail === "string"
        ? detail
        : null;

  if (response.status === 429) {
    throw new AgentApiError(
      message || "Usage limit reached",
      429,
      typeof structured.code === "string" ? structured.code : "USAGE_LIMIT_REACHED",
      structured.usage ?? null,
    );
  }
  if (response.status === 403) {
    throw new AgentApiError(
      message || "Access denied",
      403,
      typeof structured.code === "string" ? structured.code : "ACCESS_DENIED",
    );
  }
  throw new AgentApiError(
    message || `HTTP error! status: ${response.status}`,
    response.status,
    "HTTP_ERROR",
  );
}

export interface StreamAgentTurnOptions {
  /** aborting it cancels the request and ends the generator */
  signal?: AbortSignal;
  /** Clerk bearer token; fetched from the global getter when omitted */
  token?: string | null;
}

/**
 * Run one agent turn and yield its typed events in order.
 *
 * The generator ends after the terminal `done` or `error` event, when the body
 * closes, or when `options.signal` aborts — and in every one of those cases the
 * reader lock is released in the `finally`, so the response body is never left
 * locked to a dead consumer.
 */
export async function* streamAgentTurn(
  request: AgentChatRequest,
  options: StreamAgentTurnOptions = {},
): AsyncGenerator<AgentEvent, void, unknown> {
  const { signal } = options;
  const token = options.token !== undefined ? options.token : await getAuthToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${api.defaults.baseURL}${STREAM_PATH}`, {
    method: "POST",
    headers,
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    await raiseHttpError(response);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new AgentApiError("No response body reader available", response.status, "NO_BODY");
  }

  // Cancelling the reader is what unblocks a pending read(): without it an
  // abort during a quiet stretch of the stream would not be noticed until the
  // server sent its next frame.
  const onAbort = () => {
    void reader.cancel().catch(() => undefined);
  };
  if (signal) {
    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener("abort", onAbort);
    }
  }

  const decoder = new TextDecoder();
  const parser = new SseFrameParser();

  try {
    for (;;) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch (error) {
        if (isAbort(error, signal)) {
          return;
        }
        throw error;
      }
      if (chunk.done) {
        break;
      }
      if (signal?.aborted) {
        return;
      }

      for (const frame of parser.push(decoder.decode(chunk.value, { stream: true }))) {
        const event = frameToEvent(frame);
        if (!event) {
          continue;
        }
        yield event;
        if (isTerminal(event)) {
          return;
        }
      }
    }
  } finally {
    if (signal) {
      signal.removeEventListener("abort", onAbort);
    }
    try {
      reader.releaseLock();
    } catch {
      /* already released by a cancel that raced us */
    }
  }
}

/** The caller's agent conversations, most recent first. */
export async function listAgentConversations(
  page = 0,
  size = 20,
): Promise<AgentConversationsResponse> {
  const response = await api.get("/agent/conversations", { params: { page, size } });
  return response.data;
}

/** One conversation with its messages, citations and per-turn trace. */
export async function getAgentConversation(
  conversationId: string,
): Promise<AgentConversationDetail> {
  const response = await api.get(`/agent/conversations/${conversationId}`);
  return response.data;
}

/** Soft-delete a conversation (same semantics as the legacy route). */
export async function deactivateAgentConversation(
  conversationId: string,
): Promise<AgentDeactivateResponse> {
  const response = await api.patch(`/agent/conversations/${conversationId}/deactivate`);
  return response.data;
}

/** The tool legend: name, French description and whether the tool is enabled. */
export async function getAgentTools(): Promise<AgentToolsResponse> {
  const response = await api.get("/agent/tools");
  return response.data;
}


// --- Export ---

export interface ArtifactExport {
  blob: Blob;
  filename: string;
}

/**
 * Download one generated document of a conversation as a file.
 *
 * The server renders the persisted artifact, so the caller must wait for the
 * turn's `done` event (the reducer's `generationId`) before offering this.
 */
export async function exportAgentArtifact(
  conversationId: string,
  toolCallId: string,
  fallbackFilename: string,
  format: "docx" = "docx"
): Promise<ArtifactExport> {
  try {
    const response = await api.get(
      `/agent/conversations/${encodeURIComponent(conversationId)}/artifacts/${encodeURIComponent(toolCallId)}/export`,
      { params: { format }, responseType: "blob", timeout: 60_000 }
    );
    const { filenameFromDisposition } = await import("../download");
    const header = (response.headers as Record<string, string | undefined>)["content-disposition"];
    return { blob: response.data as Blob, filename: filenameFromDisposition(header) ?? fallbackFilename };
  } catch (error) {
    throw await blobErrorToAgentApiError(error);
  }
}

/** An axios error whose body is a Blob carries the JSON detail inside it. */
async function blobErrorToAgentApiError(error: unknown): Promise<Error> {
  if (error instanceof AgentApiError) return error;
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const status = response?.status ?? 0;
  let message = "Le téléchargement a échoué. Réessayez.";
  const data = response?.data;
  try {
    const text =
      data instanceof Blob ? await data.text() : typeof data === "string" ? data : JSON.stringify(data ?? {});
    const parsed = JSON.parse(text) as { detail?: unknown };
    if (typeof parsed.detail === "string" && parsed.detail) message = parsed.detail;
  } catch {
    // keep the generic message
  }
  return new AgentApiError(message, status, status === 403 ? "access_denied" : "internal");
}
